import React from 'react';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { DashboardInjectedProps } from '../../util/types';
import useSurveyResults from './useSurveyResults';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import SelectorPanel from './SelectorPanel';
import ResultsTable from './ResultsTable';
import NoSurveysAlert from './NoSurveysAlert';
import LoadableTemplateResult from '../../../Surveys/Components/SurveyTemplates/LoadableTemplateResult';

export const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
    height: '100%',
  },
  padding: {
    display: 'flex',
    background: theme.palette.common.white,
    borderRadius: theme.pxToRem(4),
    boxShadow: theme.boxShadows.table,
    position: 'absolute',
    left: theme.spacing(1),
    right: theme.spacing(1),
    bottom: theme.spacing(1),
    top: theme.spacing(1),
  },
  templateResult: {
    flex: 1,
    height: '100%',
  },
}));

export type SurveyResultsOwnProps = {};

export type SurveyResultsProps = SurveyResultsOwnProps & DashboardInjectedProps;

const SurveyResults: React.FC<SurveyResultsProps> = ({ patient }) => {
  const classes = useStyles({});
  const {
    primarySurveys,
    selectableResponses,
    isLoading,
    results,
    selectedPrimarySurveyId,
    selectedResponseId,
    setPrimarySurveyId,
    setResponseId,
    hasScoring,
    author,
    selectableTemplates,
    selectedTemplate,
    setSurveyTemplate,
    selectedSurveyId,
    filters,
    onFiltersChange,
    updateResponse,
  } = useSurveyResults(patient.id);

  const loader = (
    <Box fullHeight fullWidth align="center" justify="center">
      <DotLoader />
    </Box>
  );

  return (
    <Box direction="row" fullHeight fullWidth className={classes.root}>
      {isLoading ? (
        loader
      ) : !primarySurveys?.length ? (
        <NoSurveysAlert />
      ) : (
        <>
          <Box className={classes.padding}>
            <SelectorPanel
              primarySurveys={primarySurveys}
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              selectedPrimarySurveyId={selectedPrimarySurveyId}
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              selectableResponses={selectableResponses}
              setResponseId={setResponseId}
              selectPrimarySurveyId={setPrimarySurveyId}
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              selectedResponseId={selectedResponseId}
              author={author}
              selectableTemplates={selectableTemplates}
              selectedTemplate={selectedTemplate}
              setSurveyTemplate={setSurveyTemplate}
              filters={filters}
              onFiltersChange={onFiltersChange}
            />
            {selectedTemplate && selectedResponseId && selectedSurveyId ? (
              <LoadableTemplateResult
                className={classes.templateResult}
                templateId={selectedTemplate.templateId}
                surveyId={selectedSurveyId}
                responseId={selectedResponseId}
              />
            ) : (
              <ResultsTable
                hasScoring={hasScoring}
                totalResult={results}
                selectedResponse={selectableResponses?.find(
                  response => response.id == selectedResponseId,
                )}
                onFavoriteUpdate={updateResponse}
              />
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default React.memo(SurveyResults);
